import { CYCLE_CREATION_STATUS, CYCLE_STAGES } from './constants.js';

/** Fields allowed on the lite cycle list and the client-switch handoff. */
export const LITE_CYCLE_FIELDS = [
  '_id',
  'description',
  'from',
  'to',
  'stage',
  'stage_text',
  'status',
  'managerlist',
  'cycle_creation_status',
];

export const CYCLE_LIST_LITE_HANDOFF_MAX_AGE_MS = 60 * 1000;

export function cycleListLiteHandoffKey(clientId) {
  return `fc:cycleListLite:${clientId}`;
}

export function pickLiteCycleFields(cycle) {
  if (!cycle || typeof cycle !== 'object') return {};
  const lite = {};
  LITE_CYCLE_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(cycle, key)) {
      lite[key] = cycle[key];
    }
  });
  return lite;
}

/**
 * Most recent open cycle. Open means stage is not Closed and status is true.
 * Failed creation is skipped. in_progress is allowed. Null when none qualify.
 * @param {Array<object>|null|undefined} cycles
 * @returns {object|null}
 */
export function selectLandingCycle(cycles) {
  if (!Array.isArray(cycles) || cycles.length === 0) return null;
  const openCycles = cycles.filter((cycle) => {
    if (!cycle || typeof cycle !== 'object') return false;
    if (cycle.cycle_creation_status === CYCLE_CREATION_STATUS.FAILED) return false;
    if (cycle.stage === CYCLE_STAGES.CLOSED) return false;
    if (cycle.status !== true) return false;
    return true;
  });
  if (openCycles.length === 0) return null;
  const sorted = openCycles.slice().sort((a, b) => {
    const timeA = new Date(a.to).getTime() || 0;
    const timeB = new Date(b.to).getTime() || 0;
    return timeB - timeA;
  });
  return sorted[0];
}

/**
 * One-shot handoff written immediately before a client-switch full reload.
 * @param {Storage} storage
 * @param {string} clientId
 * @param {Array<object>} cycles
 * @param {number} [now]
 */
export function writeCycleListLiteHandoff(storage, clientId, cycles, now = Date.now()) {
  if (!storage || !clientId) return;
  const payload = {
    clientId: String(clientId),
    savedAt: now,
    cycles: (Array.isArray(cycles) ? cycles : []).map(pickLiteCycleFields),
  };
  try {
    storage.setItem(cycleListLiteHandoffKey(clientId), JSON.stringify(payload));
  } catch (_) {
    /* storage unavailable */
  }
}

/**
 * Read the handoff for this client and delete the key, whether the payload is used or not.
 * Returns the lite cycles only when clientId matches and savedAt is under 60 seconds.
 * @param {Storage} storage
 * @param {string} clientId
 * @param {number} [now]
 * @returns {Array<object>|null}
 */
export function consumeCycleListLiteHandoff(storage, clientId, now = Date.now()) {
  if (!storage || !clientId) return null;
  const key = cycleListLiteHandoffKey(clientId);
  let raw = null;
  try {
    raw = storage.getItem(key);
    storage.removeItem(key);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!parsed || String(parsed.clientId) !== String(clientId)) return null;
  const savedAt = Number(parsed.savedAt);
  const age = now - savedAt;
  if (!Number.isFinite(age) || age < 0 || age >= CYCLE_LIST_LITE_HANDOFF_MAX_AGE_MS) return null;
  if (!Array.isArray(parsed.cycles) || parsed.cycles.length === 0) return null;
  return parsed.cycles.map(pickLiteCycleFields);
}
